import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { GetCountries, StateSelect } from 'react-country-state-city';
import DatePicker from 'react-datepicker';
import { CalendarDays, Check, CheckCircle2, ChevronDown, Search, Sparkles, X } from 'lucide-react';
import 'react-country-state-city/dist/react-country-state-city.css';
import 'react-datepicker/dist/react-datepicker.css';
import { ianaTimezones } from '../../../constants';
import './teacher_match.css';

const dimensionFilters = [
  {
    key: 'emotional_tone',
    label: 'Emotional Tone',
    options: ['Warm', 'Calm', 'Energetic', 'Motivating']
  },
  {
    key: 'teaching_style',
    label: 'Teaching Style',
    options: ['Detailed', 'Simple', 'Flowing', 'Disciplined']
  },
  {
    key: 'coaching_style',
    label: 'Coaching Style',
    options: ['Supportive', 'Balanced', 'Challenging', 'Accountability-Focused']
  },
  {
    key: 'class_energy',
    label: 'Class Energy',
    options: ['Gentle', 'Moderate', 'Strong', 'Intense']
  },
  {
    key: 'personality',
    label: 'Personality',
    options: ['Friendly', 'Professional', 'Playful', 'Reserved']
  },
  {
    key: 'philosophy',
    label: 'Philosophy',
    options: ['Traditional', 'Mindful', 'Holistic', 'Fitness-Focused']
  }
];

const operationalFilters = [
  {
    key: 'country',
    label: 'Country',
    options: ['India', 'United States', 'Canada', 'United Kingdom']
  },
  {
    key: 'state',
    label: 'State',
    options: [
      'Karnataka',
      'Maharashtra',
      'Tamil Nadu',
      'Delhi',
      'California',
      'Texas',
      'New York',
      'Ontario',
      'British Columbia',
      'England'
    ]
  },
  {
    key: 'language',
    label: 'Language spoken',
    options: [
      'Afrikaans',
      'Arabic',
      'Armenian',
      'Assamese',
      'Azerbaijani',
      'Belarusian',
      'Bengali',
      'Bodo',
      'Bosnian',
      'Bulgarian',
      'Catalan',
      'Cebuano',
      'Chichewa',
      'Croatian',
      'Czech',
      'Danish',
      'Dogri',
      'Dutch',
      'English',
      'Estonian',
      'Filipino',
      'Finnish',
      'French',
      'Galician',
      'Georgian',
      'German',
      'Greek',
      'Gujarati',
      'Hausa',
      'Hebrew',
      'Hindi',
      'Hungarian',
      'Icelandic',
      'Indonesian',
      'Irish',
      'Italian',
      'Japanese',
      'Javanese',
      'Kannada',
      'Kashmiri',
      'Kazakh',
      'Kirghiz',
      'Konkani',
      'Korean',
      'Latvian',
      'Lingala',
      'Lithuanian',
      'Luxembourgish',
      'Macedonian',
      'Maithili',
      'Malay',
      'Malayalam',
      'Mandarin Chinese',
      'Manipuri',
      'Marathi',
      'Nepali',
      'Norwegian',
      'Odia',
      'Pashto',
      'Persian',
      'Polish',
      'Portuguese',
      'Punjabi',
      'Romanian',
      'Russian',
      'Sanskrit',
      'Santali',
      'Serbian',
      'Sindhi',
      'Slovak',
      'Slovenian',
      'Somali',
      'Spanish',
      'Swahili',
      'Swedish',
      'Tamil',
      'Telugu',
      'Thai',
      'Turkish',
      'Ukrainian',
      'Urdu',
      'Vietnamese',
      'Welsh'
    ]
  },
  {
    key: 'experience',
    label: 'Years Experience',
    options: ['0-5', '5-10', '10-20', '20+']
  },
  {
    key: 'goal',
    label: 'Goal Supported',
    options: [
      'Build Strength',
      'Increase Flexibility',
      'Boost Energy Levels',
      'Reduce Aches and Pain',
      'Stress Relief',
      'Lose Weight',
      'Manage a Health Condition',
      'Sports Injury',
      'Prenatal or Postnatal'
    ]
  },
  { key: 'gender', label: 'Gender', options: ['Female', 'Male'] },
  {
    key: 'age',
    label: 'Age',
    options: ['20-25', '25-30', '30-35', '35-40', '40-50', '50-60', '60+']
  },
  { key: 'availability', label: 'Availability', options: ['Morning', 'Afternoon', 'Evening'] },
  { key: 'conversion', label: 'Conversion', options: ['Great', 'Medium', 'Poor'] },
  {
    key: 'platform_years',
    label: 'Years on Platform',
    options: ['< 3 months', '3-6 months', '6 months - year', '1-3 years', '3+ years']
  },
  {
    key: 'base_rate',
    label: 'Teacher Base Rate',
    options: ['less than 500', '500-599', '600-699', '700-799', '799+']
  },
  { key: 'trial', label: 'Trial Session', options: ['Offers trial sessions'] }
];

const sessionFilters = operationalFilters.filter((filter) => filter.key === 'trial');

const sessionDurationFilter = {
  key: 'session_duration',
  label: 'Session Duration (Mins)',
  options: ['30', '60', '45', '90']
};

const recurringDays = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' }
];

const EXPERIENCE_MIN = 0;
const EXPERIENCE_MAX = 20;

function formatExperienceValue(years) {
  return years >= EXPERIENCE_MAX ? `${EXPERIENCE_MAX}+` : `${years}`;
}

function isExperienceDefault([min, max]) {
  return min <= EXPERIENCE_MIN && max >= EXPERIENCE_MAX;
}

// Teacher experience is stored as a bucket string ('0-5', '10-20', '20+').
// Convert it to a numeric [low, high] range so it can be compared to the slider.
function parseExperienceBucket(bucket) {
  if (bucket.endsWith('+')) return [parseInt(bucket, 10), Infinity];
  const [low, high] = bucket.split('-').map(Number);
  return [low, high];
}

const BASE_RATE_MIN = 0;
const BASE_RATE_MAX = 800;

function formatBaseRateValue(rate) {
  return rate >= BASE_RATE_MAX ? '799+' : `${rate}`;
}

function isBaseRateDefault([min, max]) {
  return min <= BASE_RATE_MIN && max >= BASE_RATE_MAX;
}

// Teacher base rate is a bucket string ('less than 500', '600-699', '799+').
function parseBaseRateBucket(bucket) {
  if (bucket.startsWith('less than')) return [0, Number(bucket.replace(/\D/g, ''))];
  if (bucket.endsWith('+')) return [parseInt(bucket, 10), Infinity];
  const [low, high] = bucket.split('-').map(Number);
  return [low, high];
}

const AGE_MIN = 20;
const AGE_MAX = 60;

function formatAgeValue(age) {
  return age >= AGE_MAX ? `${AGE_MAX}+` : `${age}`;
}

function isAgeDefault([min, max]) {
  return min <= AGE_MIN && max >= AGE_MAX;
}

// Teacher age is a bucket string ('20-25', '40-50', '60+').
function parseAgeBucket(bucket) {
  if (bucket.endsWith('+')) return [parseInt(bucket, 10), Infinity];
  const [low, high] = bucket.split('-').map(Number);
  return [low, high];
}

// True when the selected [min, max] slider range overlaps a teacher's bucket.
// The slider max acts as an open-ended "+", and a shared boundary alone doesn't
// count as overlap — unless the user picked a single point (min === max).
function rangeMatchesBucket([selectedMin, selectedMax], sliderMax, [bucketLow, bucketHigh]) {
  const upperBound = selectedMax >= sliderMax ? Infinity : selectedMax;
  const overlapLow = Math.max(bucketLow, selectedMin);
  const overlapHigh = Math.min(bucketHigh, upperBound);
  return overlapLow < overlapHigh || (overlapLow === overlapHigh && selectedMin === selectedMax);
}

// Years on Platform is a stepped (ordinal) slider — each step is one of the
// dropdown options, in ascending order. The slider value is a [minIndex, maxIndex].
const platformYearsSteps = operationalFilters.find(
  (filter) => filter.key === 'platform_years'
).options;
const PLATFORM_YEARS_MIN = 0;
const PLATFORM_YEARS_MAX = platformYearsSteps.length - 1;

function formatPlatformYears(index) {
  return platformYearsSteps[index];
}

function isPlatformYearsDefault([min, max]) {
  return min <= PLATFORM_YEARS_MIN && max >= PLATFORM_YEARS_MAX;
}

const availabilityWindows = [
  { key: 'early_morning', label: 'Early Morning (5:00 AM – 7:00 AM)' },
  { key: 'morning', label: 'Morning (7:00 AM – 11:00 AM)' },
  { key: 'afternoon', label: 'Afternoon (11:00 AM – 4:00 PM)' },
  { key: 'early_evening', label: 'Early Evening (4:00 PM – 7:00 PM)' },
  { key: 'evening', label: 'Evening (7:00 PM – 10:00 PM)' },
  { key: 'late_night', label: 'Late Night (10:00 PM onwards)' }
];

const exactTimeOptions = Array.from({ length: 37 }, (_, index) => {
  const totalMinutes = 4 * 60 + index * 30;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return {
    value: `${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`,
    label: `${`${displayHour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')} ${period}`
  };
});

const timezoneOptions = ianaTimezones.flatMap((group) =>
  group.options.map((option) => ({
    ...option,
    group: group.label
  }))
);

// The country filter is limited to the countries represented in ianaTimezones,
// identified by their iso2 codes.
const TIMEZONE_COUNTRY_ISO2 = new Set(
  ianaTimezones.flatMap((group) => group.options.map((option) => option.iso2)).filter(Boolean)
);

function getInitialFilters() {
  return {
    ...dimensionFilters.reduce((acc, filter) => ({ ...acc, [filter.key]: [] }), {}),
    ...operationalFilters.reduce(
      (acc, filter) => ({
        ...acc,
        [filter.key]: ['goal', 'language'].includes(filter.key) ? [] : 'All'
      }),
      {}
    ),
    experience: [EXPERIENCE_MIN, EXPERIENCE_MAX],
    base_rate: [BASE_RATE_MIN, BASE_RATE_MAX],
    platform_years: [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX],
    age: [AGE_MIN, AGE_MAX],
    timezone: 'America/Los_Angeles',
    availability_windows: [],
    availability_exact_times: [],
    session_duration: '60',
    session_date: new Date(),
    recurring_days: []
  };
}

const teachers = [
  {
    uuid: '1e8a8cf1-b7c6-4a2a-8e68-6b2e50dd2c3f',
    name: 'Dushyant M',
    location: 'Karnataka, India',
    country: 'India',
    state: 'Karnataka',
    timezone: 'Asia/Kolkata',
    language: 'English, Hindi, Kannada',
    experience: '10-20',
    gender: 'Male',
    age: '35-40',
    availability: 'Morning',
    session_durations: ['30', '60'],
    session_dates: ['2026-06-17', '2026-06-20'],
    recurring_session: 'At same day & time every week',
    conversion: 'Great',
    platform_years: '3+ years',
    base_rate: '600-699',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 94,
    next_slot: 'Tomorrow, 7:30 AM IST',
    goals: ['Reduce Aches and Pain', 'Increase Flexibility', 'Stress Relief'],
    profile: {
      emotional_tone: 'Motivating',
      teaching_style: 'Detailed',
      coaching_style: 'Supportive',
      class_energy: 'Moderate',
      personality: 'Professional',
      philosophy: 'Mindful'
    }
  },
  {
    uuid: '9afce322-0bf1-4b60-a05e-c724efb3a3f6',
    name: 'Priya S',
    location: 'Maharashtra, India',
    country: 'India',
    state: 'Maharashtra',
    timezone: 'Asia/Kolkata',
    language: 'English, Hindi',
    experience: '5-10',
    gender: 'Female',
    age: '30-35',
    availability: 'Evening',
    session_durations: ['30', '45', '60'],
    session_dates: ['2026-06-16', '2026-06-18'],
    recurring_session: 'At same day & time every week',
    conversion: 'Great',
    platform_years: '1-3 years',
    base_rate: '700-799',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 91,
    next_slot: 'Today, 6:00 PM IST',
    goals: ['Lose Weight', 'Boost Energy Levels', 'Build Strength'],
    profile: {
      emotional_tone: 'Energetic',
      teaching_style: 'Flowing',
      coaching_style: 'Challenging',
      class_energy: 'Strong',
      personality: 'Playful',
      philosophy: 'Fitness-Focused'
    }
  },
  {
    uuid: '2f4d35ff-cb1a-4f66-8705-46e4763025e4',
    name: 'Anand V',
    location: 'California, United States',
    country: 'United States',
    state: 'California',
    timezone: 'America/Los_Angeles',
    language: 'English, Spanish',
    experience: '20+',
    gender: 'Male',
    age: '50-60',
    availability: 'Morning',
    session_durations: ['60', '90'],
    session_dates: ['2026-06-19', '2026-06-22'],
    recurring_session: '',
    conversion: 'Medium',
    platform_years: '3+ years',
    base_rate: '799+',
    thirty_min: '',
    trial: 'Offers trial sessions',
    match_score: 87,
    next_slot: 'Friday, 8:00 AM PT',
    goals: ['Stress Relief', 'Manage a Health Condition', 'Increase Flexibility'],
    profile: {
      emotional_tone: 'Warm',
      teaching_style: 'Detailed',
      coaching_style: 'Balanced',
      class_energy: 'Gentle',
      personality: 'Friendly',
      philosophy: 'Traditional'
    }
  },
  {
    uuid: '7349f55d-e86c-4dc4-9a47-a6fbd8ab3b6f',
    name: 'Meera R',
    location: 'Texas, United States',
    country: 'United States',
    state: 'Texas',
    timezone: 'America/Chicago',
    language: 'English, Tamil',
    experience: '10-20',
    gender: 'Female',
    age: '40-50',
    availability: 'Afternoon',
    session_durations: ['30', '45'],
    session_dates: ['2026-06-18', '2026-06-21'],
    recurring_session: 'At same day & time every week',
    conversion: 'Medium',
    platform_years: '1-3 years',
    base_rate: '600-699',
    thirty_min: 'Offers 30 minute session',
    trial: '',
    match_score: 83,
    next_slot: 'Thursday, 1:30 PM CT',
    goals: ['Prenatal or Postnatal', 'Manage a Health Condition', 'Stress Relief'],
    profile: {
      emotional_tone: 'Calm',
      teaching_style: 'Simple',
      coaching_style: 'Supportive',
      class_energy: 'Gentle',
      personality: 'Reserved',
      philosophy: 'Holistic'
    }
  },
  {
    uuid: '4ad2a969-3f05-46d2-978e-a09ded8992b3',
    name: 'Neha K',
    location: 'Ontario, Canada',
    country: 'Canada',
    state: 'Ontario',
    timezone: 'America/Toronto',
    language: 'English, Hindi',
    experience: '0-5',
    gender: 'Female',
    age: '25-30',
    availability: 'Evening',
    session_durations: ['30', '60'],
    session_dates: ['2026-06-22', '2026-06-24'],
    recurring_session: '',
    conversion: 'Poor',
    platform_years: '3-6 months',
    base_rate: '500-599',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 78,
    next_slot: 'Monday, 7:00 PM ET',
    goals: ['Lose Weight', 'Boost Energy Levels', 'Build Strength'],
    profile: {
      emotional_tone: 'Motivating',
      teaching_style: 'Disciplined',
      coaching_style: 'Accountability-Focused',
      class_energy: 'Intense',
      personality: 'Professional',
      philosophy: 'Fitness-Focused'
    }
  },
  {
    uuid: '6bd76f55-742b-4d35-9062-84ca6d8997af',
    name: 'Rohan P',
    location: 'Delhi, India',
    country: 'India',
    state: 'Delhi',
    timezone: 'Asia/Kolkata',
    language: 'English, Hindi, Punjabi',
    experience: '5-10',
    gender: 'Male',
    age: '30-35',
    availability: 'Afternoon',
    session_durations: ['30', '60'],
    session_dates: ['2026-06-16', '2026-06-19'],
    recurring_session: 'At same day & time every week',
    conversion: 'Great',
    platform_years: '6 months - year',
    base_rate: '500-599',
    thirty_min: 'Offers 30 minute session',
    trial: '',
    match_score: 89,
    next_slot: 'Today, 3:30 PM IST',
    goals: ['Build Strength', 'Lose Weight', 'Boost Energy Levels'],
    profile: {
      emotional_tone: 'Motivating',
      teaching_style: 'Disciplined',
      coaching_style: 'Challenging',
      class_energy: 'Strong',
      personality: 'Professional',
      philosophy: 'Fitness-Focused'
    }
  },
  {
    uuid: 'b774559b-9c40-4b72-99c9-f3debf0f82d8',
    name: 'Kavya N',
    location: 'Tamil Nadu, India',
    country: 'India',
    state: 'Tamil Nadu',
    timezone: 'Asia/Kolkata',
    language: 'English, Tamil',
    experience: '10-20',
    gender: 'Female',
    age: '35-40',
    availability: 'Morning',
    session_durations: ['45', '60'],
    session_dates: ['2026-06-17', '2026-06-23'],
    recurring_session: '',
    conversion: 'Great',
    platform_years: '3+ years',
    base_rate: '700-799',
    thirty_min: '',
    trial: 'Offers trial sessions',
    match_score: 92,
    next_slot: 'Tomorrow, 6:15 AM IST',
    goals: ['Prenatal or Postnatal', 'Manage a Health Condition', 'Increase Flexibility'],
    profile: {
      emotional_tone: 'Warm',
      teaching_style: 'Simple',
      coaching_style: 'Supportive',
      class_energy: 'Gentle',
      personality: 'Friendly',
      philosophy: 'Holistic'
    }
  },
  {
    uuid: '0d80d6d7-b343-47d1-80fe-d43d2e3314b0',
    name: 'Sofia L',
    location: 'New York, United States',
    country: 'United States',
    state: 'New York',
    timezone: 'America/New_York',
    language: 'English, Spanish',
    experience: '5-10',
    gender: 'Female',
    age: '25-30',
    availability: 'Evening',
    session_durations: ['30', '45', '90'],
    session_dates: ['2026-06-16', '2026-06-25'],
    recurring_session: 'At same day & time every week',
    conversion: 'Medium',
    platform_years: '1-3 years',
    base_rate: '799+',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 85,
    next_slot: 'Today, 8:00 PM ET',
    goals: ['Stress Relief', 'Reduce Aches and Pain', 'Increase Flexibility'],
    profile: {
      emotional_tone: 'Calm',
      teaching_style: 'Flowing',
      coaching_style: 'Balanced',
      class_energy: 'Moderate',
      personality: 'Reserved',
      philosophy: 'Mindful'
    }
  },
  {
    uuid: '5c2ef52d-910b-47b1-a489-19afbd58ee47',
    name: 'James W',
    location: 'England, United Kingdom',
    country: 'United Kingdom',
    state: 'England',
    timezone: 'Europe/London',
    language: 'English',
    experience: '20+',
    gender: 'Male',
    age: '60+',
    availability: 'Afternoon',
    session_durations: ['60', '90'],
    session_dates: ['2026-06-19', '2026-06-26'],
    recurring_session: 'At same day & time every week',
    conversion: 'Great',
    platform_years: '3+ years',
    base_rate: '799+',
    thirty_min: '',
    trial: 'Offers trial sessions',
    match_score: 88,
    next_slot: 'Friday, 4:00 PM GMT',
    goals: ['Stress Relief', 'Increase Flexibility', 'Manage a Health Condition'],
    profile: {
      emotional_tone: 'Warm',
      teaching_style: 'Detailed',
      coaching_style: 'Balanced',
      class_energy: 'Gentle',
      personality: 'Professional',
      philosophy: 'Traditional'
    }
  },
  {
    uuid: 'fd98b52e-3406-4a78-8a4f-a7f74fd4c735',
    name: 'Aisha Q',
    location: 'British Columbia, Canada',
    country: 'Canada',
    state: 'British Columbia',
    timezone: 'America/Vancouver',
    language: 'English, French',
    experience: '0-5',
    gender: 'Female',
    age: '20-25',
    availability: 'Evening',
    session_durations: ['30', '45'],
    session_dates: ['2026-06-18', '2026-06-27'],
    recurring_session: '',
    conversion: 'Medium',
    platform_years: '< 3 months',
    base_rate: 'less than 500',
    thirty_min: 'Offers 30 minute session',
    trial: '',
    match_score: 76,
    next_slot: 'Wednesday, 5:30 PM PT',
    goals: ['Lose Weight', 'Boost Energy Levels', 'Build Strength'],
    profile: {
      emotional_tone: 'Energetic',
      teaching_style: 'Flowing',
      coaching_style: 'Accountability-Focused',
      class_energy: 'Intense',
      personality: 'Playful',
      philosophy: 'Fitness-Focused'
    }
  },
  {
    uuid: '08921f60-f427-42c7-9f49-e49444d259b9',
    name: 'Vikram T',
    location: 'California, United States',
    country: 'United States',
    state: 'California',
    timezone: 'America/Los_Angeles',
    language: 'English, Telugu',
    experience: '10-20',
    gender: 'Male',
    age: '40-50',
    availability: 'Morning',
    session_durations: ['30', '60', '90'],
    session_dates: ['2026-06-23', '2026-06-29'],
    recurring_session: 'At same day & time every week',
    conversion: 'Poor',
    platform_years: '6 months - year',
    base_rate: '600-699',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 73,
    next_slot: 'Monday, 9:00 AM PT',
    goals: ['Increase Flexibility', 'Sports Injury', 'Reduce Aches and Pain'],
    profile: {
      emotional_tone: 'Motivating',
      teaching_style: 'Detailed',
      coaching_style: 'Supportive',
      class_energy: 'Moderate',
      personality: 'Friendly',
      philosophy: 'Mindful'
    }
  },
  {
    uuid: 'd63ee9e3-37a0-4908-8eea-b30df62c1bd9',
    name: 'Maya B',
    location: 'Ontario, Canada',
    country: 'Canada',
    state: 'Ontario',
    timezone: 'America/Toronto',
    language: 'English, Hindi',
    experience: '5-10',
    gender: 'Female',
    age: '30-35',
    availability: 'Afternoon',
    session_durations: ['45', '60'],
    session_dates: ['2026-06-24', '2026-06-30'],
    recurring_session: '',
    conversion: 'Great',
    platform_years: '1-3 years',
    base_rate: '600-699',
    thirty_min: '',
    trial: 'Offers trial sessions',
    match_score: 86,
    next_slot: 'Thursday, 12:00 PM ET',
    goals: ['Stress Relief', 'Manage a Health Condition', 'Reduce Aches and Pain'],
    profile: {
      emotional_tone: 'Calm',
      teaching_style: 'Simple',
      coaching_style: 'Balanced',
      class_energy: 'Gentle',
      personality: 'Reserved',
      philosophy: 'Holistic'
    }
  },
  {
    uuid: '94e05fa5-8e4f-4d9f-8a7f-36e5f625038a',
    name: 'Harini S',
    location: 'Karnataka, India',
    country: 'India',
    state: 'Karnataka',
    timezone: 'Asia/Kolkata',
    language: 'English, Kannada, Telugu',
    experience: '20+',
    gender: 'Female',
    age: '50-60',
    availability: 'Morning',
    session_durations: ['30', '90'],
    session_dates: ['2026-06-20', '2026-06-28'],
    recurring_session: 'At same day & time every week',
    conversion: 'Medium',
    platform_years: '3+ years',
    base_rate: '700-799',
    thirty_min: 'Offers 30 minute session',
    trial: 'Offers trial sessions',
    match_score: 90,
    next_slot: 'Saturday, 8:45 AM IST',
    goals: ['Stress Relief', 'Prenatal or Postnatal', 'Increase Flexibility'],
    profile: {
      emotional_tone: 'Warm',
      teaching_style: 'Detailed',
      coaching_style: 'Supportive',
      class_energy: 'Moderate',
      personality: 'Professional',
      philosophy: 'Traditional'
    }
  }
];

function getInitials(name) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function profileTags(profile) {
  return dimensionFilters
    .map((filter) => ({
      key: filter.key,
      label: filter.label.replace('Emotional ', '').replace(' Style', ''),
      value: profile[filter.key]
    }))
    .filter((tag) => tag.value);
}

function LabeledPill({ label, value, tone = 'neutral', icon = null }) {
  return (
    <span className={`teacher-match-labeled-pill teacher-match-labeled-pill-${tone}`}>
      {icon ? <span className="teacher-match-pill-icon">{icon}</span> : null}
      <span className="teacher-match-pill-label">{label}</span>
      <span className="teacher-match-pill-value">{value}</span>
    </span>
  );
}

LabeledPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  tone: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  icon: PropTypes.node
};

function getFilterTriggerLabel(value) {
  if (!Array.isArray(value)) return value;
  if (value.length === 0) return 'All';
  if (value.length <= 2) return value.join(', ');
  return `${value.length} selected`;
}

// Decide whether an options menu should open upward, based on the room left
// below the trigger within the viewport. Re-measures whenever the menu opens.
function useDropDirection(ref, isOpen, menuHeight = 240) {
  const [dropUp, setDropUp] = useState(false);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    setDropUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
  }, [ref, isOpen, menuHeight]);

  return dropUp;
}

function getTimezoneLabel(value) {
  if (value === 'All') return 'All';
  const timezone = timezoneOptions.find((option) => option.value === value);
  return timezone ? `${timezone.label} - ${timezone.value}` : value;
}

function getAvailabilityWindowLabel(value) {
  return availabilityWindows.find((window) => window.key === value)?.label || value;
}

function getExactTimeLabel(value) {
  return exactTimeOptions.find((time) => time.value === value)?.label || value;
}

function getTeacherAvailabilityWindows(teacher) {
  if (teacher.availability === 'Morning') return ['early_morning', 'morning'];
  if (teacher.availability === 'Afternoon') return ['afternoon'];
  if (teacher.availability === 'Evening') return ['early_evening', 'evening', 'late_night'];
  return [];
}

function getTeacherExactTimes(teacher) {
  const windows = getTeacherAvailabilityWindows(teacher);
  return exactTimeOptions
    .filter((time) => {
      const hour = Number(time.value.slice(0, 2));
      if (windows.includes('early_morning') && hour >= 5 && hour < 7) return true;
      if (windows.includes('morning') && hour >= 7 && hour < 11) return true;
      if (windows.includes('afternoon') && hour >= 11 && hour < 16) return true;
      if (windows.includes('early_evening') && hour >= 16 && hour < 19) return true;
      if (windows.includes('evening') && hour >= 19 && hour < 22) return true;
      if (windows.includes('late_night') && hour >= 22) return true;
      return false;
    })
    .map((time) => time.value);
}

function formatDateKey(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateLabel(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function getActiveFilterChips(filters, searchValue) {
  const searchChip = searchValue.trim()
    ? [{ key: 'search', label: 'Search', value: searchValue.trim(), filterKey: 'search' }]
    : [];

  const dimensionChips = dimensionFilters.flatMap((filter) =>
    filters[filter.key].map((value) => ({
      key: `${filter.key}-${value}`,
      label: filter.label.replace('Emotional ', '').replace(' Style', ''),
      value,
      filterKey: filter.key
    }))
  );

  const operationalChips = operationalFilters.flatMap((filter) => {
    if (['experience', 'base_rate', 'platform_years', 'age'].includes(filter.key)) return [];
    const value = filters[filter.key];
    if (Array.isArray(value)) {
      return value.map((selectedValue) => ({
        key: `${filter.key}-${selectedValue}`,
        label: filter.label,
        value: selectedValue,
        filterKey: filter.key
      }));
    }

    return value !== 'All'
      ? [
          {
            key: filter.key,
            label: filter.label,
            value,
            filterKey: filter.key
          }
        ]
      : [];
  });

  const timezoneChip =
    filters.timezone !== 'All'
      ? [
          {
            key: 'timezone',
            label: 'Timezone',
            value: getTimezoneLabel(filters.timezone),
            filterKey: 'timezone'
          }
        ]
      : [];

  const sessionDurationChip =
    filters.session_duration && filters.session_duration !== 'All'
      ? [
          {
            key: 'session-duration',
            label: 'Duration',
            value: `${filters.session_duration} mins`,
            filterKey: 'session_duration'
          }
        ]
      : [];

  const sessionDateChip = filters.session_date
    ? [
        {
          key: 'session-date',
          label: 'Session date',
          value: formatDateLabel(filters.session_date),
          filterKey: 'session_date'
        }
      ]
    : [];

  const experienceRange = filters.experience || [EXPERIENCE_MIN, EXPERIENCE_MAX];
  const experienceChip = isExperienceDefault(experienceRange)
    ? []
    : [
        {
          key: 'experience',
          label: 'Experience',
          value: `${formatExperienceValue(experienceRange[0])} - ${formatExperienceValue(experienceRange[1])} yrs`,
          filterKey: 'experience'
        }
      ];

  const baseRateRange = filters.base_rate || [BASE_RATE_MIN, BASE_RATE_MAX];
  const baseRateChip = isBaseRateDefault(baseRateRange)
    ? []
    : [
        {
          key: 'base_rate',
          label: 'Base rate',
          value: `${formatBaseRateValue(baseRateRange[0])} - ${formatBaseRateValue(baseRateRange[1])}`,
          filterKey: 'base_rate'
        }
      ];

  const platformYearsRange = filters.platform_years || [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX];
  const platformYearsChip = isPlatformYearsDefault(platformYearsRange)
    ? []
    : [
        {
          key: 'platform_years',
          label: 'Years on platform',
          value: `${formatPlatformYears(platformYearsRange[0])} - ${formatPlatformYears(platformYearsRange[1])}`,
          filterKey: 'platform_years'
        }
      ];

  const ageRange = filters.age || [AGE_MIN, AGE_MAX];
  const ageChip = isAgeDefault(ageRange)
    ? []
    : [
        {
          key: 'age',
          label: 'Age',
          value: `${formatAgeValue(ageRange[0])} - ${formatAgeValue(ageRange[1])}`,
          filterKey: 'age'
        }
      ];

  const recurringDayChips = (filters.recurring_days || []).map((dayKey) => ({
    key: `recurring-day-${dayKey}`,
    label: 'Day',
    value: recurringDays.find((day) => day.key === dayKey)?.label || dayKey,
    rawValue: dayKey,
    filterKey: 'recurring_days'
  }));

  const availabilityWindowChips = (filters.availability_windows || []).map((value) => ({
    key: `availability-window-${value}`,
    label: 'Availability',
    value: getAvailabilityWindowLabel(value),
    rawValue: value,
    filterKey: 'availability_windows'
  }));

  const availabilityExactTimeChips = (filters.availability_exact_times || []).map((value) => ({
    key: `availability-exact-${value}`,
    label: 'Exact time',
    value: getExactTimeLabel(value),
    rawValue: value,
    filterKey: 'availability_exact_times'
  }));

  return [
    ...searchChip,
    ...dimensionChips,
    ...operationalChips,
    ...experienceChip,
    ...baseRateChip,
    ...platformYearsChip,
    ...ageChip,
    ...availabilityWindowChips,
    ...availabilityExactTimeChips,
    ...timezoneChip,
    ...sessionDurationChip,
    ...sessionDateChip,
    ...recurringDayChips
  ];
}

function resetDocumentScroll() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function FilterDropdown({ filter, value, isOpen, onToggle, onChange, searchable }) {
  const ref = useRef(null);
  const [query, setQuery] = useState('');
  const isMultiSelect = Array.isArray(value);
  const selectedValues = isMultiSelect ? value : [];
  const hasSelection = isMultiSelect ? selectedValues.length > 0 : value !== 'All';
  const triggerLabel = getFilterTriggerLabel(value);
  const dropUp = useDropDirection(ref, isOpen);

  const normalizedQuery = query.trim().toLowerCase();
  const options = normalizedQuery
    ? filter.options.filter((option) => option.toLowerCase().includes(normalizedQuery))
    : ['All', ...filter.options];

  const isOptionSelected = (option) => {
    if (isMultiSelect)
      return option === 'All' ? selectedValues.length === 0 : selectedValues.includes(option);
    return value === option;
  };

  const selectOption = (option) => {
    if (!isMultiSelect) {
      onChange(filter.key, option);
      onToggle('');
      return;
    }

    if (option === 'All') {
      onChange(filter.key, []);
      return;
    }

    onChange(
      filter.key,
      selectedValues.includes(option)
        ? selectedValues.filter((selectedValue) => selectedValue !== option)
        : [...selectedValues, option]
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className={`teacher-match-field ${isOpen ? 'teacher-match-field-open' : ''}`} ref={ref}>
      <div className="teacher-match-field-label">{filter.label}</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${hasSelection ? 'teacher-match-selected' : ''}`}
        onClick={() => onToggle(isOpen ? '' : filter.key)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{triggerLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className={`teacher-match-options ${dropUp ? 'teacher-match-options-up' : ''}`}
          role="listbox"
          aria-multiselectable={isMultiSelect}
        >
          {searchable ? (
            <div className="teacher-match-timezone-search">
              <Search size={14} />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${filter.label.toLowerCase()}`}
              />
            </div>
          ) : null}
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className="teacher-match-option"
              onClick={() => selectOption(option)}
              role="option"
              aria-selected={isOptionSelected(option)}
            >
              <span>{option}</span>
              {isOptionSelected(option) ? <Check size={14} /> : null}
            </button>
          ))}
          {searchable && options.length === 0 ? (
            <div className="teacher-match-option-empty">No matches</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FilterCheckbox({ filter, value, onChange }) {
  const isChecked = value === filter.options[0];
  const inputId = `teacher-match-${filter.key}`;

  return (
    <label
      className={`teacher-match-checkbox ${isChecked ? 'teacher-match-checkbox-selected' : ''}`}
      htmlFor={inputId}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={isChecked}
        onChange={(event) => onChange(filter.key, event.target.checked ? filter.options[0] : 'All')}
      />
      <span className="teacher-match-checkbox-control">
        {isChecked ? <Check size={14} /> : null}
      </span>
      <span className="teacher-match-checkbox-text">{filter.label}</span>
    </label>
  );
}

function FilterSegmentedControl({ filter, value, onChange, className }) {
  const options = ['All', ...filter.options];

  return (
    <div className={`teacher-match-field ${className}`}>
      <div className="teacher-match-field-label">{filter.label}</div>
      <div
        className="teacher-match-segmented"
        role="group"
        aria-label={filter.label}
        style={{ '--segment-count': options.length }}
      >
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`teacher-match-segment ${value === option ? 'teacher-match-segment-selected' : ''}`}
            onClick={() => onChange(filter.key, option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function RecurringDaysSelector({ value, onChange }) {
  const toggleDay = (key) => {
    onChange(
      'recurring_days',
      value.includes(key) ? value.filter((d) => d !== key) : [...value, key]
    );
  };

  return (
    <div className="teacher-match-field">
      <div className="teacher-match-field-label">Days of week</div>
      <div className="teacher-match-day-toggle" role="group" aria-label="Days of week">
        {recurringDays.map((day) => (
          <button
            key={day.key}
            type="button"
            className={`teacher-match-day ${value.includes(day.key) ? 'teacher-match-day-selected' : ''}`}
            onClick={() => toggleDay(day.key)}
            aria-pressed={value.includes(day.key)}
          >
            {day.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RangeSlider({ filterKey, label, value, min, max, step, formatValue, unit, onChange }) {
  const [minValue, maxValue] = value;
  const percent = (current) => ((current - min) / (max - min)) * 100;

  const handleMinChange = (event) => {
    const next = Math.min(Number(event.target.value), maxValue);
    onChange(filterKey, [next, maxValue]);
  };

  const handleMaxChange = (event) => {
    const next = Math.max(Number(event.target.value), minValue);
    onChange(filterKey, [minValue, next]);
  };

  return (
    <div className="teacher-match-field">
      <div className="teacher-match-range-header">
        <div className="teacher-match-field-label">{label}</div>
        <div className="teacher-match-range-readout">
          {formatValue(minValue)} – {formatValue(maxValue)}
          {unit ? ` ${unit}` : ''}
        </div>
      </div>
      <div className="teacher-match-range">
        <div className="teacher-match-range-track">
          <div
            className="teacher-match-range-fill"
            style={{ left: `${percent(minValue)}%`, right: `${100 - percent(maxValue)}%` }}
          />
        </div>
        <input
          type="range"
          className="teacher-match-range-input"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={handleMinChange}
          aria-label={`${label} minimum`}
        />
        <input
          type="range"
          className="teacher-match-range-input"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleMaxChange}
          aria-label={`${label} maximum`}
        />
      </div>
    </div>
  );
}

function AvailabilityTimeFilter({ windows, exactTimes, isOpen, onToggle, onChange }) {
  const [showExactTimes, setShowExactTimes] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const [draftWindows, setDraftWindows] = useState(windows);
  const [draftExactTimes, setDraftExactTimes] = useState(exactTimes);
  const ref = useRef(null);
  const selectedCount = windows.length + exactTimes.length;
  const triggerLabel = selectedCount === 0 ? 'All' : `${selectedCount} selected`;

  useEffect(() => {
    if (!isOpen) return;
    setDraftWindows(windows);
    setDraftExactTimes(exactTimes);
  }, [exactTimes, isOpen, windows]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const updatePopoverPosition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const popoverWidth = 360;
      const popoverHeight = showExactTimes
        ? Math.min(620, window.innerHeight - 32)
        : Math.min(360, window.innerHeight - 32);
      const preferredLeft = rect.right + 18;
      const left =
        preferredLeft + popoverWidth <= window.innerWidth - 16
          ? preferredLeft
          : Math.max(16, window.innerWidth - popoverWidth - 16);
      const top = Math.max(
        16,
        Math.min(rect.top, Math.max(16, window.innerHeight - popoverHeight - 16))
      );

      setPopoverStyle({
        left,
        top
      });
    };

    updatePopoverPosition();
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, onToggle, showExactTimes]);

  const toggleTimePopover = () => {
    if (isOpen) {
      onToggle('');
      return;
    }

    const scrollContainer = ref.current?.closest('.teacher-match-filter-scroll');
    if (ref.current && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const fieldRect = ref.current.getBoundingClientRect();
      const nextScrollTop = scrollContainer.scrollTop + fieldRect.top - containerRect.top - 14;
      scrollContainer.scrollTo({ top: Math.max(0, nextScrollTop) });
    } else {
      ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    window.requestAnimationFrame(() => onToggle('availability'));
  };

  const toggleDraftValue = (selectedValues, value, updateSelectedValues) => {
    updateSelectedValues(
      selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value]
    );
  };

  const applyTimeFilters = () => {
    onChange('availability_windows', draftWindows);
    onChange('availability_exact_times', draftExactTimes);
    onToggle('');
  };

  return (
    <div className="teacher-match-field teacher-match-availability-field" ref={ref}>
      <div className="teacher-match-field-label">Session Time</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${selectedCount > 0 ? 'teacher-match-selected' : ''}`}
        onClick={toggleTimePopover}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>{triggerLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className="teacher-match-availability-popover"
          role="dialog"
          aria-label="Session Time"
          style={popoverStyle}
        >
          <div className="teacher-match-availability-popover-title">
            Classes between these times
          </div>

          <div className="teacher-match-time-choice-list">
            {availabilityWindows.map((window) => (
              <label
                key={window.key}
                htmlFor={`teacher-match-availability-${window.key}`}
                className={`teacher-match-time-choice ${draftWindows.includes(window.key) ? 'teacher-match-time-choice-selected' : ''}`}
              >
                <input
                  id={`teacher-match-availability-${window.key}`}
                  type="checkbox"
                  checked={draftWindows.includes(window.key)}
                  onChange={() => toggleDraftValue(draftWindows, window.key, setDraftWindows)}
                />
                <span className="teacher-match-time-choice-box">
                  {draftWindows.includes(window.key) ? <Check size={14} /> : null}
                </span>
                <span>{window.label}</span>
              </label>
            ))}
          </div>

          <button
            type="button"
            className="teacher-match-exact-time-toggle"
            onClick={() => setShowExactTimes((current) => !current)}
          >
            <span>Or choose an exact time</span>
            <ChevronDown size={16} className={showExactTimes ? 'teacher-match-chevron-open' : ''} />
          </button>

          {showExactTimes ? (
            <div className="teacher-match-exact-time-list">
              {exactTimeOptions.map((time) => (
                <label
                  key={time.value}
                  htmlFor={`teacher-match-exact-time-${time.value.replace(':', '-')}`}
                  className={`teacher-match-time-choice ${draftExactTimes.includes(time.value) ? 'teacher-match-time-choice-selected' : ''}`}
                >
                  <input
                    id={`teacher-match-exact-time-${time.value.replace(':', '-')}`}
                    type="checkbox"
                    checked={draftExactTimes.includes(time.value)}
                    onChange={() =>
                      toggleDraftValue(draftExactTimes, time.value, setDraftExactTimes)
                    }
                  />
                  <span className="teacher-match-time-choice-box">
                    {draftExactTimes.includes(time.value) ? <Check size={14} /> : null}
                  </span>
                  <span>{time.label}</span>
                </label>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            className="teacher-match-availability-apply"
            onClick={applyTimeFilters}
          >
            Apply Filter
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TimezoneFilterDropdown({ value, isOpen, onToggle, onChange }) {
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selectedLabel = getTimezoneLabel(value);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = timezoneOptions.filter((option) => {
    const searchable = `${option.group} ${option.label} ${option.value}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  const hasSelection = value !== 'All';
  const dropUp = useDropDirection(ref, isOpen, 280);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const selectTimezone = (nextValue) => {
    onChange('timezone', nextValue);
    onToggle('');
  };

  return (
    <div className={`teacher-match-field ${isOpen ? 'teacher-match-field-open' : ''}`} ref={ref}>
      <div className="teacher-match-field-label">Timezone</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${hasSelection ? 'teacher-match-selected' : ''}`}
        onClick={() => onToggle(isOpen ? '' : 'timezone')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className={`teacher-match-options teacher-match-timezone-options ${dropUp ? 'teacher-match-options-up' : ''}`}
          role="listbox"
        >
          <div className="teacher-match-timezone-search">
            <Search size={14} />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search timezone"
            />
          </div>
          <button
            type="button"
            className="teacher-match-option"
            onClick={() => selectTimezone('All')}
            role="option"
            aria-selected={value === 'All'}
          >
            <span>All</span>
            {value === 'All' ? <Check size={14} /> : null}
          </button>
          {filteredOptions.map((option) => (
            <button
              key={`${option.group}-${option.value}`}
              type="button"
              className="teacher-match-option teacher-match-timezone-option"
              onClick={() => selectTimezone(option.value)}
              role="option"
              aria-selected={value === option.value}
            >
              <span>
                <span className="teacher-match-timezone-option-label">{option.label}</span>
                <span className="teacher-match-timezone-option-value">{option.value}</span>
              </span>
              {value === option.value ? <Check size={14} /> : null}
            </button>
          ))}
          {filteredOptions.length === 0 ? (
            <div className="teacher-match-option-empty">No timezones found</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SessionDateFilter({ value = null, onChange }) {
  return (
    <div className="teacher-match-field teacher-match-date-field">
      <div className="teacher-match-field-label">Session Date</div>
      <div className="teacher-match-datepicker-control">
        <CalendarDays size={14} />
        <DatePicker
          selected={value}
          onChange={(date) => onChange('session_date', date)}
          placeholderText="Any date"
          dateFormat="MMM d, yyyy"
          minDate={new Date()}
          isClearable
          wrapperClassName="teacher-match-datepicker-wrapper"
          className="teacher-match-datepicker-input"
          popperClassName="teacher-match-datepicker-popper"
        />
      </div>
    </div>
  );
}

function GeoFilterControls({
  countryValue,
  stateValue,
  allowedCountries,
  selectedCountry = null,
  selectVersion,
  openFilter,
  onToggle,
  onCountryChange,
  onStateChange
}) {
  const countryFilter = {
    key: 'country',
    label: 'Country',
    options: allowedCountries.map((country) => country.name)
  };

  return (
    <>
      <FilterDropdown
        filter={countryFilter}
        value={countryValue}
        isOpen={openFilter === 'country'}
        onToggle={onToggle}
        onChange={(key, value) => onCountryChange(value)}
        searchable
      />

      <div className="teacher-match-field">
        <div className="teacher-match-field-label">State</div>
        <StateSelect
          key={`state-${selectedCountry?.id || 'none'}-${selectVersion}`}
          containerClassName="teacher-match-geo-select"
          inputClassName="teacher-match-geo-input"
          placeHolder={selectedCountry ? 'All states' : 'Select country first'}
          countryid={selectedCountry?.id || 0}
          defaultValue={stateValue !== 'All' ? stateValue : undefined}
          disabled={!selectedCountry}
          onChange={onStateChange}
        />
      </div>
    </>
  );
}

FilterDropdown.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  searchable: PropTypes.bool
};

FilterDropdown.defaultProps = {
  searchable: false
};

FilterCheckbox.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

FilterSegmentedControl.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string
};

FilterSegmentedControl.defaultProps = {
  className: ''
};

RecurringDaysSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired
};

RangeSlider.propTypes = {
  filterKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  formatValue: PropTypes.func.isRequired,
  unit: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

AvailabilityTimeFilter.propTypes = {
  windows: PropTypes.arrayOf(PropTypes.string).isRequired,
  exactTimes: PropTypes.arrayOf(PropTypes.string).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

TimezoneFilterDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

SessionDateFilter.propTypes = {
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired
};

GeoFilterControls.propTypes = {
  countryValue: PropTypes.string.isRequired,
  stateValue: PropTypes.string.isRequired,
  allowedCountries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      iso2: PropTypes.string
    })
  ).isRequired,
  // eslint-disable-next-line react/require-default-props
  selectedCountry: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    iso2: PropTypes.string
  }),
  selectVersion: PropTypes.number.isRequired,
  openFilter: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired
};

function TeacherMatch() {
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(getInitialFilters);
  const [openFilter, setOpenFilter] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [geoSelectVersion, setGeoSelectVersion] = useState(0);
  const [allowedCountries, setAllowedCountries] = useState([]);
  const resultsScrollRef = useRef(null);
  const activeFilterChips = getActiveFilterChips(filters, searchValue);

  useEffect(() => {
    resetDocumentScroll();
    const frame = window.requestAnimationFrame(resetDocumentScroll);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  // Load the country list once, limited to the countries present in ianaTimezones.
  useEffect(() => {
    let active = true;
    Promise.resolve(GetCountries()).then((all) => {
      if (!active) return;
      const allowed = all
        .filter((country) => TIMEZONE_COUNTRY_ISO2.has(country.iso2))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllowedCountries(allowed);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    resultsScrollRef.current?.scrollTo({ top: 0 });
  }, [filters, searchValue]);

  const selectedFilterCount = activeFilterChips.length;

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const setCountryFilter = (countryName) => {
    setGeoSelectVersion((version) => version + 1);
    if (!countryName || countryName === 'All') {
      setSelectedCountry(null);
      setFilters((prev) => ({ ...prev, country: 'All', state: 'All' }));
      return;
    }
    const country = allowedCountries.find((item) => item.name === countryName) || null;
    setSelectedCountry(country);
    setFilters((prev) => ({ ...prev, country: countryName, state: 'All' }));
  };

  const setStateFilter = (state) => setFilter('state', state.name);

  const removeFilterChip = (chip) => {
    if (chip.filterKey === 'search') {
      setSearchValue('');
      return;
    }

    if (chip.filterKey === 'country') {
      setSelectedCountry(null);
      setGeoSelectVersion((version) => version + 1);
      setFilters((prev) => ({ ...prev, country: 'All', state: 'All' }));
      return;
    }

    if (chip.filterKey === 'state') {
      setGeoSelectVersion((version) => version + 1);
      setFilter('state', 'All');
      return;
    }

    if (chip.filterKey === 'session_date') {
      setFilter('session_date', null);
      return;
    }

    if (chip.filterKey === 'experience') {
      setFilter('experience', [EXPERIENCE_MIN, EXPERIENCE_MAX]);
      return;
    }

    if (chip.filterKey === 'base_rate') {
      setFilter('base_rate', [BASE_RATE_MIN, BASE_RATE_MAX]);
      return;
    }

    if (chip.filterKey === 'platform_years') {
      setFilter('platform_years', [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX]);
      return;
    }

    if (chip.filterKey === 'age') {
      setFilter('age', [AGE_MIN, AGE_MAX]);
      return;
    }

    setFilters((prev) => {
      const currentValue = prev[chip.filterKey];
      if (Array.isArray(currentValue)) {
        return {
          ...prev,
          [chip.filterKey]: currentValue.filter(
            (selectedValue) => selectedValue !== (chip.rawValue || chip.value)
          )
        };
      }

      return { ...prev, [chip.filterKey]: 'All' };
    });
  };

  const clearFilters = () => {
    setSearchValue('');
    // Reset clears everything — including the timezone and session date that
    // getInitialFilters seeds with defaults.
    setFilters({ ...getInitialFilters(), timezone: 'All', session_date: null });
    setOpenFilter('');
    setSelectedCountry(null);
    setGeoSelectVersion((version) => version + 1);
  };

  const filteredTeachers = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return teachers.filter((teacher) => {
      const { profile } = teacher;
      const matchesSearch =
        !query ||
        teacher.name.toLowerCase().includes(query) ||
        teacher.location.toLowerCase().includes(query) ||
        teacher.language.toLowerCase().includes(query) ||
        teacher.goals.some((goal) => goal.toLowerCase().includes(query));

      const matchesDimensions = dimensionFilters.every((filter) => {
        const values = filters[filter.key];
        return values.length === 0 || values.includes(profile[filter.key]);
      });

      const matchesOperations = operationalFilters.every((filter) => {
        if (['experience', 'base_rate', 'platform_years', 'age'].includes(filter.key)) return true;
        const value = filters[filter.key];
        if (Array.isArray(value)) {
          if (value.length === 0) return true;
          if (filter.key === 'goal')
            return value.some((selectedGoal) => teacher.goals.includes(selectedGoal));
          if (filter.key === 'language') {
            const teacherLanguages = teacher.language.split(',').map((lang) => lang.trim());
            return value.some((selectedLang) => teacherLanguages.includes(selectedLang));
          }
          return value.includes(teacher[filter.key]);
        }
        if (value === 'All') return true;
        if (filter.key === 'goal') return teacher.goals.includes(value);
        return teacher[filter.key] === value;
      });

      const experienceRange = filters.experience || [EXPERIENCE_MIN, EXPERIENCE_MAX];
      const matchesExperience =
        isExperienceDefault(experienceRange) ||
        rangeMatchesBucket(
          experienceRange,
          EXPERIENCE_MAX,
          parseExperienceBucket(teacher.experience)
        );

      const baseRateRange = filters.base_rate || [BASE_RATE_MIN, BASE_RATE_MAX];
      const matchesBaseRate =
        isBaseRateDefault(baseRateRange) ||
        rangeMatchesBucket(baseRateRange, BASE_RATE_MAX, parseBaseRateBucket(teacher.base_rate));

      const platformYearsRange = filters.platform_years || [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX];
      const teacherPlatformIndex = platformYearsSteps.indexOf(teacher.platform_years);
      const matchesPlatformYears =
        isPlatformYearsDefault(platformYearsRange) ||
        (teacherPlatformIndex >= platformYearsRange[0] &&
          teacherPlatformIndex <= platformYearsRange[1]);

      const ageRange = filters.age || [AGE_MIN, AGE_MAX];
      const matchesAge =
        isAgeDefault(ageRange) ||
        rangeMatchesBucket(ageRange, AGE_MAX, parseAgeBucket(teacher.age));

      const matchesTimezone = filters.timezone === 'All' || teacher.timezone === filters.timezone;
      const matchesSessionDuration =
        !filters.session_duration ||
        filters.session_duration === 'All' ||
        teacher.session_durations.includes(filters.session_duration);
      const matchesSessionDate =
        !filters.session_date ||
        teacher.session_dates.includes(formatDateKey(filters.session_date));
      const selectedAvailabilityWindows = filters.availability_windows || [];
      const selectedExactTimes = filters.availability_exact_times || [];
      const teacherAvailabilityWindows = getTeacherAvailabilityWindows(teacher);
      const teacherExactTimes = getTeacherExactTimes(teacher);
      const matchesAvailabilityWindows =
        selectedAvailabilityWindows.length === 0 ||
        selectedAvailabilityWindows.some((window) => teacherAvailabilityWindows.includes(window));
      const matchesExactTimes =
        selectedExactTimes.length === 0 ||
        selectedExactTimes.some((time) => teacherExactTimes.includes(time));

      return (
        matchesSearch &&
        matchesDimensions &&
        matchesOperations &&
        matchesExperience &&
        matchesBaseRate &&
        matchesPlatformYears &&
        matchesAge &&
        matchesAvailabilityWindows &&
        matchesExactTimes &&
        matchesTimezone &&
        matchesSessionDuration &&
        matchesSessionDate
      );
    });
  }, [filters, searchValue]);

  const bestMatch = filteredTeachers[0];

  return (
    <div className="teacher-match-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Teacher Match</title>
      </Helmet>

      <div className="teacher-match-grid">
        <aside className="teacher-match-filter-panel">
          <div className="teacher-match-panel-heading">
            <div>
              <div className="teacher-match-panel-title">Filters</div>
              <div className="teacher-match-muted">{selectedFilterCount} selected</div>
            </div>
          </div>

          <div className="teacher-match-filter-scroll">
            <div className="teacher-match-filter-group">
              <div className="teacher-match-filter-heading">Personality Dimensions</div>
              {dimensionFilters.map((filter) => (
                <FilterDropdown
                  key={filter.key}
                  filter={filter}
                  value={filters[filter.key]}
                  isOpen={openFilter === filter.key}
                  onToggle={setOpenFilter}
                  onChange={setFilter}
                />
              ))}
            </div>

            <div className="teacher-match-filter-group teacher-match-operational-group">
              <div className="teacher-match-filter-heading">Operational Filters</div>
              <GeoFilterControls
                countryValue={filters.country}
                stateValue={filters.state}
                allowedCountries={allowedCountries}
                selectedCountry={selectedCountry}
                selectVersion={geoSelectVersion}
                openFilter={openFilter}
                onToggle={setOpenFilter}
                onCountryChange={setCountryFilter}
                onStateChange={setStateFilter}
              />
              {operationalFilters.map((filter) => {
                if (filter.key === 'country' || filter.key === 'state') return null;
                if (filter.key === 'trial') return null;

                if (filter.key === 'availability') {
                  return (
                    <div key={filter.key} className="teacher-match-availability-section">
                      <div className="teacher-match-availability-title">Availability</div>
                      <div className="teacher-match-availability-subfilters">
                        {sessionFilters.map((sessionFilter) => (
                          <FilterCheckbox
                            key={sessionFilter.key}
                            filter={sessionFilter}
                            value={filters[sessionFilter.key]}
                            onChange={setFilter}
                          />
                        ))}
                        <TimezoneFilterDropdown
                          value={filters.timezone}
                          isOpen={openFilter === 'timezone'}
                          onToggle={setOpenFilter}
                          onChange={setFilter}
                        />
                        <SessionDateFilter
                          value={filters.session_date || null}
                          onChange={setFilter}
                        />
                        <AvailabilityTimeFilter
                          windows={filters.availability_windows || []}
                          exactTimes={filters.availability_exact_times || []}
                          isOpen={openFilter === 'availability'}
                          onToggle={setOpenFilter}
                          onChange={setFilter}
                        />
                        <FilterSegmentedControl
                          filter={sessionDurationFilter}
                          value={filters.session_duration || 'All'}
                          onChange={setFilter}
                        />
                        <RecurringDaysSelector
                          value={filters.recurring_days || []}
                          onChange={setFilter}
                        />
                      </div>
                    </div>
                  );
                }

                if (filter.key === 'experience') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="experience"
                      label={filter.label}
                      value={filters.experience}
                      min={EXPERIENCE_MIN}
                      max={EXPERIENCE_MAX}
                      step={1}
                      formatValue={formatExperienceValue}
                      unit="yrs"
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'base_rate') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="base_rate"
                      label={filter.label}
                      value={filters.base_rate}
                      min={BASE_RATE_MIN}
                      max={BASE_RATE_MAX}
                      step={50}
                      formatValue={formatBaseRateValue}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'platform_years') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="platform_years"
                      label={filter.label}
                      value={filters.platform_years}
                      min={PLATFORM_YEARS_MIN}
                      max={PLATFORM_YEARS_MAX}
                      step={1}
                      formatValue={formatPlatformYears}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'age') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="age"
                      label={filter.label}
                      value={filters.age}
                      min={AGE_MIN}
                      max={AGE_MAX}
                      step={5}
                      formatValue={formatAgeValue}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'gender' || filter.key === 'conversion') {
                  return (
                    <FilterSegmentedControl
                      key={filter.key}
                      filter={filter}
                      value={filters[filter.key]}
                      onChange={setFilter}
                      className={filter.key === 'conversion' ? 'teacher-match-field-divider' : ''}
                    />
                  );
                }

                return filter.options.length === 1 ? (
                  <FilterCheckbox
                    key={filter.key}
                    filter={filter}
                    value={filters[filter.key]}
                    onChange={setFilter}
                  />
                ) : (
                  <FilterDropdown
                    key={filter.key}
                    filter={filter}
                    value={filters[filter.key]}
                    isOpen={openFilter === filter.key}
                    onToggle={setOpenFilter}
                    onChange={setFilter}
                    searchable={filter.key === 'language'}
                  />
                );
              })}
            </div>
          </div>
        </aside>

        <section className="teacher-match-results">
          <div className="teacher-match-results-top">
            <div className="teacher-match-results-title">Teacher Matches</div>
            <div className="teacher-match-toolbar">
              <div className="teacher-match-search">
                <Search size={16} />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search teacher, location, language, or goal"
                />
              </div>
              <button
                type="button"
                className="teacher-match-clear-btn"
                onClick={clearFilters}
                title="Reset filters"
              >
                <X size={14} />
                <span>Reset</span>
              </button>
            </div>

            {activeFilterChips.length > 0 ? (
              <div className="teacher-match-active-filters">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    className="teacher-match-filter-chip"
                    onClick={() => removeFilterChip(chip)}
                    title={`Remove ${chip.label}: ${chip.value}`}
                  >
                    <span className="teacher-match-filter-chip-label">{chip.label}</span>
                    <span className="teacher-match-filter-chip-value">{chip.value}</span>
                    <X size={13} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="teacher-match-results-scroll" ref={resultsScrollRef}>
            {bestMatch ? (
              <div className="teacher-match-recommendation">
                <div className="teacher-match-recommendation-icon">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="teacher-match-eyebrow">Best match now</div>
                  <div className="teacher-match-recommendation-title">
                    {bestMatch.name} - {bestMatch.match_score}% match
                  </div>
                  <div className="teacher-match-muted">
                    {profileTags(bestMatch.profile)
                      .map((tag) => `${tag.label}: ${tag.value}`)
                      .join(' - ')}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="teacher-match-results-header">
              <div>
                <div className="teacher-match-section-title">Teachers</div>
                <div className="teacher-match-muted">
                  Showing {filteredTeachers.length} of {teachers.length}
                </div>
              </div>
            </div>

            {filteredTeachers.length === 0 ? (
              <div className="teacher-match-empty">No teachers match the selected filters.</div>
            ) : (
              <div className="teacher-match-list">
                {filteredTeachers.map((teacher) => (
                  <article key={teacher.uuid} className="teacher-match-card">
                    <div className="teacher-match-card-main">
                      <div className="teacher-match-avatar">{getInitials(teacher.name)}</div>
                      <div>
                        <div className="teacher-match-card-title-row">
                          <Link
                            to={`/teacherprofile?id=${encodeURIComponent(teacher.uuid)}`}
                            className="teacher-match-name"
                          >
                            {teacher.name}
                          </Link>
                          <span className="teacher-match-score">{teacher.match_score}%</span>
                        </div>
                        <div className="teacher-match-muted">{teacher.location}</div>
                        <div className="teacher-match-tags">
                          {profileTags(teacher.profile).map((tag) => (
                            <LabeledPill
                              key={tag.key}
                              label={tag.label}
                              value={tag.value}
                              tone="dimension"
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="teacher-match-meta">
                      <LabeledPill
                        label="Conversion"
                        value={teacher.conversion}
                        tone="meta"
                        icon={<CheckCircle2 size={15} />}
                      />
                      <LabeledPill
                        label="Next slot"
                        value={teacher.next_slot}
                        tone="meta"
                        icon={<CalendarDays size={15} />}
                      />
                      <LabeledPill
                        label="Experience"
                        value={`${teacher.experience} yrs`}
                        tone="meta"
                      />
                      <LabeledPill label="Base rate" value={teacher.base_rate} tone="meta" />
                    </div>

                    <div className="teacher-match-goals">
                      {teacher.goals.map((goal) => (
                        <LabeledPill key={goal} label="Goal" value={goal} tone="goal" />
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default TeacherMatch;
